import './styles/ExecutionGraph.scss';

import { useClassHelper } from '@whistler-ui/k2/hooks';
import { Maybe } from '@whistler-ui/k2/types';
import React, { CSSProperties, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TEST_STATUSES, USE_CASE_TAGS } from '../../constants';
import {
  GRAPH_CASES,
  GRAPH_HEADERS,
  GRAPH_LABELS,
} from '../../constants/graphConstants';
import { ApiTestExecution, TUseCaseTags } from '../../types';
import { DeviceIconRenderer } from './components/DeviceIconRenderer';

const componentName = 'c-executionGraph';

export type TargetSourceHeader = (
  | typeof GRAPH_HEADERS.TARGET_SOURCE
  | 'graph_header_device'
)[number];

interface Props {
  activeItem: Maybe<string>;
  errorKey: string | undefined;
  setActiveItem: (label: string) => void;
  status: string;
  tagName: string;
  websheetKey: boolean;
  useCaseTag: TUseCaseTags;
  executions: ApiTestExecution[];
}

export const ExecutionGraph = ({
  errorKey,
  websheetKey,
  activeItem,
  setActiveItem,
  tagName,
  status,
  useCaseTag,
  executions,
}: Props) => {
  const isWatchUseCaseTests = useCaseTag === USE_CASE_TAGS.Watch_Activation;
  const labelsRef = useRef({});
  const c = useClassHelper(componentName);
  const { t } = useTranslation();

  const activeLabels = React.useMemo(() => {
    return executions.map(({ test_case_tag }) => {
      const graphLabel = GRAPH_LABELS[test_case_tag];
      return graphLabel || test_case_tag;
    });
  }, [executions]);

  const headers = GRAPH_HEADERS[tagName as keyof typeof GRAPH_HEADERS];
  const useCases = GRAPH_CASES[tagName as keyof typeof GRAPH_CASES];

  const errorIndex = useRef(-1);
  const creationIndex = useRef(-1);

  const failedExecution = [
    TEST_STATUSES.CANCELLED,
    TEST_STATUSES.FAILED,
  ].includes(status as any);

  const onDeselect = () => {
    setActiveItem(null);
  };

  const handleClick = ({ currentTarget: target }) => {
    setActiveItem(activeItem !== target.dataset.id ? target.dataset.id : null);
  };

  const getLabelOrIcon = (label: TargetSourceHeader) => {
    switch (label) {
      case 'graph_header_source_device':
      case 'graph_header_device': {
        return (
          <div>
            <DeviceIconRenderer device="phone" />
            <span>
              {t(
                isWatchUseCaseTests
                  ? 'graph_header_source_device_watch'
                  : label,
              )}
            </span>
          </div>
        );
      }

      case 'graph_header_target_device': {
        return (
          <div>
            <DeviceIconRenderer
              device={isWatchUseCaseTests ? 'watch' : 'phone'}
            />
            <span>
              {t(
                isWatchUseCaseTests
                  ? 'graph_header_target_device_watch'
                  : label,
              )}
            </span>
          </div>
        );
      }

      default: {
        return (
          <div>
            <span>{t(label)}</span>
          </div>
        );
      }
    }
  };

  if (headers === undefined) {
    return null;
  } else {
    return (
      <div
        className={c('base')}
        style={
          {
            '--headers': headers.length,
            '--cases': useCases.length,
            '--rows': useCases[useCases.length - 1].row,
          } as CSSProperties
        }
      >
        <div className={c('__headers')}>
          {headers.map((label: string, i: number) => {
            labelsRef.current[label] = i + 1;

            const labelOrIcon = getLabelOrIcon(label);

            return (
              <div
                className={`${c('__header')} ${label}`}
                onClick={onDeselect}
                key={label}
                role="button"
              >
                {labelOrIcon}
              </div>
            );
          })}
        </div>

        <div className={c('__columns')}>
          {headers.map((label: any) => (
            <div
              className={`${c('__column')} ${label}`}
              onClick={onDeselect}
              key={label}
              role="button"
            />
          ))}
        </div>
        <div className={c('__cases')}>
          {useCases.map((caseParams: any, i: number) => {
            if (websheetKey === caseParams.label) {
              creationIndex.current = i;
            } else if (errorKey === caseParams.label) {
              errorIndex.current = i;
            }

            const [className, styles] = getStepStyles(
              caseParams,
              activeItem,
              creationIndex,
              websheetKey,
              errorIndex,
              errorKey,
              labelsRef,
              failedExecution,
              activeLabels,
              i,
            );
            return (
              styles && (
                <button
                  type="button"
                  key={`${caseParams.label}-${i}`}
                  style={styles as React.CSSProperties}
                  className={className as string}
                  onClick={handleClick}
                  disabled={/(skip|default)/.test(className as string)}
                  data-id={caseParams.label}
                >
                  <span>{!caseParams.hide ? t(caseParams.label) : ''}</span>
                </button>
              )
            );
          })}
        </div>
      </div>
    );
  }
};

const setClassType = (
  type = 'default',
  errorIndex,
  creationIndex,
  activeLabels,
  label,
  i,
) => {
  const isCreated = creationIndex.current >= 0 && i > creationIndex.current;
  const hasError = errorIndex.current >= 0 && i > errorIndex.current;
  let classType = type;

  if (isCreated) {
    classType = 'default-skip';
  }
  if (hasError || (type === 'primary' && !activeLabels.includes(label))) {
    classType = 'skip';
  }
  return classType;
};

const getStepStyles = (
  { label, type, start, end, row },
  activeItem,
  creationIndex,
  websheetKey,
  errorIndex,
  errorKey,
  labelsRef,
  failedExecution,
  activeLabels,
  i,
) => {
  const classType = setClassType(
    type,
    errorIndex,
    creationIndex,
    activeLabels,
    label,
    i,
  );
  let styles = {
    '--start': labelsRef.current[start],
    '--end': labelsRef.current[end] || end,
    '--row': row,
  };
  const reversedClass = () =>
    styles['--start'] > styles['--end'] ? 'reversed' : '';

  if (styles['--start'] === undefined || styles['--end'] === undefined) {
    styles = null;
  }
  const isActiveClass = () => (activeItem === label ? 'active' : '');
  const isErrorKey = () => (errorKey === label ? 'error' : '');
  const iswebsheetKey = () =>
    websheetKey === label && !failedExecution
      ? 'websheet'
      : websheetKey === label && failedExecution
      ? 'websheet-failed'
      : '';

  const className = `${componentName}__case ${label} ${classType} ${reversedClass()}
  ${isActiveClass()} ${isErrorKey()} ${iswebsheetKey()}`;

  return [className, styles];
};
